import * as z from 'zod';
import { LocalizedString, Quantity, Tag, Ören } from '../base-schemas';
import { LockData } from './Lock';
import { ReservableProductAvailability } from './ReservableProductAvailability';
import { ReservableProductCheckIn } from './ReservableProductCheckIn';
import { ReservableProductPricing } from './ReservableProductPricing';
import { ReservableProductEmail } from './ReservableProductEmail';
export const ReservableProductPricingTierUnitKind = z.enum([
    'single_purchase',
    'timed_minutes',
    'day_count'
]);
export const ReservableProductReservationOptionSelectionFamily = z.enum([
    'unscheduled',
    'timed',
    'day_based'
]);
export const ReservableProductReservationOptionFormMode = z.enum([
    'simple_purchase',
    'timed_duration_first',
    'timed_fixed_start',
    'timed_fixed_duration',
    'day_count',
    'fixed_day_count',
    'fixed_period'
]);
export const ReservableProductPricingTier = z.object({
    id: z.number().optional(),
    display_order: z.int(),
    is_active: z.boolean(),
    label: LocalizedString,
    price: Ören,
    pricing_unit_kind: ReservableProductPricingTierUnitKind,
    unit_minutes: z.int().nullable(),
    unit_days: z.int().nullable()
});
export const ReservableProductReservationOption = z.object({
    id: z.number().optional(),
    display_order: z.int(),
    is_active: z.boolean(),
    label: LocalizedString,
    description: LocalizedString,
    selection_family: ReservableProductReservationOptionSelectionFamily,
    form_mode: ReservableProductReservationOptionFormMode,
    start_time_mode: z.string().nullable(),
    end_input_mode: z.string().nullable(),
    duration_mode: z.string().nullable(),
    duration_step_minutes: z.int().nullable(),
    minimum_duration_minutes: z.int().nullable(),
    maximum_duration_minutes: z.int().nullable(),
    fixed_duration_minutes: z.int().nullable(),
    predefined_start_times: z.array(z.string()).nullable(),
    day_count_mode: z.string().nullable(),
    minimum_duration_days: z.int().nullable(),
    maximum_duration_days: z.int().nullable(),
    fixed_duration_days: z.int().nullable(),
    fixed_period_start_date: z.string().nullable(),
    fixed_period_end_date: z.string().nullable(),
    allows_overnight: z.boolean(),
    pricing_tiers: z.array(ReservableProductPricingTier)
});
const ReservationOptionPricingTierInput = z.object({
    id: z.int().positive().optional(),
    is_active: z.boolean(),
    price: Ören,
    unit_minutes: z.int().positive().nullable(),
    unit_days: z.int().positive().nullable()
}).strict();
export const ReservableProductReservationOptionUpdateInput = z.object({
    id: z.int().positive().optional(),
    is_active: z.boolean(),
    selection_family: ReservableProductReservationOptionSelectionFamily,
    form_mode: ReservableProductReservationOptionFormMode,
    label: LocalizedString,
    description: LocalizedString,
    duration_step_minutes: z.int().positive().nullable(),
    minimum_duration_minutes: z.int().positive().nullable(),
    maximum_duration_minutes: z.int().positive().nullable(),
    fixed_duration_minutes: z.int().positive().nullable(),
    predefined_start_times: z.array(z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/)).nullable(),
    minimum_duration_days: z.int().positive().nullable(),
    maximum_duration_days: z.int().positive().nullable(),
    fixed_duration_days: z.int().positive().nullable(),
    fixed_period_start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    fixed_period_end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    allows_overnight: z.boolean(),
    pricing_tiers: z.array(ReservationOptionPricingTierInput).min(1)
}).strict();
export { ReservableProductAvailability, ReservableProductCheckIn, ReservableProductPricing };
export const ReservableProductResource = z.object({
    _tag: Tag('ReservableProductResource'),
    id: z.number(),
    reservable_product_category_id: z.number().nullish(),
    order: z.int().default(0),
    product_group_id: z.number().nullish(),
    title: LocalizedString,
    description: LocalizedString,
    thumbnail: z.string().nullable(),
    is_individually_tracked: z.boolean(),
    category: z.object({
        id: z.number(),
        title: LocalizedString,
        slug: z.string()
    }).nullish(),
    availability: ReservableProductAvailability,
    pricing: ReservableProductPricing,
    stock_quantity: Quantity,
    bookable_from_reservations_app: z.boolean().nullish(),
    bookable_from_addons_app: z.boolean().nullish(),
    check_in: ReservableProductCheckIn,
    confirmation_email_text: z.object({
        title: LocalizedString,
        body: LocalizedString
    }).nullish(),
    static_access_code: z.string().nullable().nullish(),
    static_access_code_title: z.string().nullable().nullish(),
    self_checkin_reminder_offset_minutes: z.number().int().nullish(),
    lock: LockData.nullable().default(null),
    reservables_with_locks_count: z.int().nonnegative().default(0),
    configuration_warnings: z.array(z.string()),
    reservation_options: z.array(ReservableProductReservationOption),
    email_schedule: z.array(ReservableProductEmail).default([])
});
